import { env } from '@config/env';
import { DEFAULT_PAGE_COPY, type ResolvedPageCopy } from '@config/env/page-copy-defaults';
import type { PageHeroCopy, ResolvedPageHeroCopy, ResidentAppPageCopy } from '@config/env/types';

export type { ResolvedPageCopy } from '@config/env/page-copy-defaults';

function mergeHero(base: ResolvedPageHeroCopy, patch?: Partial<PageHeroCopy>): ResolvedPageHeroCopy {
  return {
    eyebrow: patch?.eyebrow ?? base.eyebrow,
    title: patch?.title ?? base.title,
    subtitle: patch?.subtitle ?? base.subtitle,
  };
}

function mergePageCopy(
  base: ResolvedPageCopy,
  patch?: ResidentAppPageCopy,
): ResolvedPageCopy {
  if (!patch) return base;

  return {
    ...base,
    more: mergeHero(base.more, patch.more),
    home: { ...base.home, ...patch.home },
    facilities: {
      ...base.facilities,
      ...patch.facilities,
      myBookings: mergeHero(base.facilities.myBookings, patch.facilities?.myBookings),
      detail: { ...base.facilities.detail, ...patch.facilities?.detail },
    },
    documents: mergeHero(base.documents, patch.documents),
    events: { ...base.events, ...patch.events },
    gallery: mergeHero(base.gallery, patch.gallery),
    newsStand: mergeHero(base.newsStand, patch.newsStand),
    suppliers: { ...base.suppliers, ...patch.suppliers },
    map: { ...base.map, ...patch.map },
    faq: mergeHero(base.faq, patch.faq),
    classifieds: {
      ...base.classifieds,
      ...patch.classifieds,
      form: {
        new: mergeHero(base.classifieds.form.new, patch.classifieds?.form?.new),
        edit: mergeHero(base.classifieds.form.edit, patch.classifieds?.form?.edit),
      },
    },
    support: {
      list: mergeHero(base.support.list, patch.support?.list),
      new: mergeHero(base.support.new, patch.support?.new),
      detailEyebrow: patch.support?.detailEyebrow ?? base.support.detailEyebrow,
    },
    profile: { ...base.profile, ...patch.profile },
    properties: { ...base.properties, ...patch.properties },
    invoices: { ...base.invoices, ...patch.invoices },
    panic: {
      sos: mergeHero(base.panic.sos, patch.panic?.sos),
      contacts: mergeHero(base.panic.contacts, patch.panic?.contacts),
      contactForm: {
        new: mergeHero(base.panic.contactForm.new, patch.panic?.contactForm?.new),
        edit: mergeHero(base.panic.contactForm.edit, patch.panic?.contactForm?.edit),
      },
    },
    surveys: mergeHero(base.surveys, patch.surveys),
    accessInvites: mergeHero(base.accessInvites, patch.accessInvites),
    pets: {
      ...mergeHero(base.pets, patch.pets),
      form: {
        new: mergeHero(base.pets.form.new, patch.pets?.form?.new),
        edit: mergeHero(base.pets.form.edit, patch.pets?.form?.edit),
      },
    },
    vehicles: {
      ...mergeHero(base.vehicles, patch.vehicles),
      form: {
        new: mergeHero(base.vehicles.form.new, patch.vehicles?.form?.new),
        edit: mergeHero(base.vehicles.form.edit, patch.vehicles?.form?.edit),
      },
    },
    auth: { ...base.auth, ...patch.auth },
    selectProperty: mergeHero(base.selectProperty, patch.selectProperty),
    management: { ...base.management, ...patch.management },
    products: { ...base.products, ...patch.products },
    settings: { ...base.settings, ...patch.settings },
    welcomeTour: patch.welcomeTour ?? base.welcomeTour,
  };
}

let cached: ResolvedPageCopy | null = null;

/** Resolved page copy for the active build (`env.pageCopy` merged over defaults). */
export function pageCopy(): ResolvedPageCopy {
  if (!cached) {
    cached = mergePageCopy(DEFAULT_PAGE_COPY, env.pageCopy);
  }
  return cached;
}

/** Replace `{name}`-style tokens in env-provided strings. */
export function interpolateCopy(
  template: string,
  vars: Record<string, string | undefined>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => vars[key]?.trim() ?? '');
}
